import {
  BackEndValueType,
  ESdChangeReason,
  ESymbolCategory,
  EWFParamType,
  ISymbol,
  IWFParameter,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { Key, ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithFormProp, WithForm } from "../../../../shared/hoc/with-form";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import PlusIcon from "../../../../svgs/small-icons/plus-icon.svg";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./work-flow-fields-panel.scss";
import { WorkflowParameterItem } from "./workflow-parameter-item";
import { WorkflowParameterItemCreate } from "./workflow-parameter-item-create";

const WORKFLOW_RESERVED_PARAM_NAMES = [
  "id",
  "typename",
  "name",
  "eventname",
  "eventkey",
  "effectivedate",
  "assignmenttype",
  "assignmentkey",
  "description",
  "duedate"
];

interface IFieldData {
  id: string;
  name?: string | number;
  dataType?: string;
  wfId?: string;
  value?: any;
  isEditing?: boolean;
  isCreating?: boolean;
  validating?: boolean;
  errors?: string[];
}

export interface IWorkFlowFieldsPanelState {
  addPanelVisible: boolean;
  isAddParamInProgress: boolean;
  paramToAdd: IWFParameter;
  activeKey: string[];
}

export interface IWorkFlowFieldsPanelProps {}

class WorkFlowFieldsPanel extends PureSdComponent<
  IWorkFlowFieldsPanelProps & IWithSDProps & IWithContextProp & IWithFormProp<IWorkFlowFieldsPanelProps>,
  IWorkFlowFieldsPanelState
> {
  dataTypes = [
    "Boolean",
    "Bytea",
    "Byte",
    "DateTimeOffset",
    "Decimal",
    "Double",
    "Guid",
    // "Int16",
    "Int32",
    "Int64",
    // "Single",
    "String",
    "Image"
  ];

  constructor(
    props: IWorkFlowFieldsPanelProps & IWithSDProps & IWithContextProp & IWithFormProp<IWorkFlowFieldsPanelProps>
  ) {
    super(props);
    this.state = {
      addPanelVisible: false,
      isAddParamInProgress: false,
      paramToAdd: {
        id: Guid.create().toString(),
        name: "",
        dataType: "String" as BackEndValueType,
        parameterType: EWFParamType.Bag,
        isInput: false,
        isOutput: false,
        defaultValue: {
          category: ESymbolCategory.fixed,
          label: "Fixed Value",
          description: ""
        }
      } as IWFParameter,
      activeKey: ["input", "internal"]
    };
  }

  handleChange = (e, field) => {
    const paramToAdd = _.clone(this.state.paramToAdd);
    if (field === "name") {
      const { value } = e.target;
      if (paramToAdd !== undefined) {
        paramToAdd.name = value;
        this.setState({ paramToAdd });
      }
    } else if (field === "type") {
      if (paramToAdd !== undefined) {
        paramToAdd.dataType = e as BackEndValueType;
        this.setState({ paramToAdd });
      }
    } else if (field === "input") {
      if (paramToAdd !== undefined) {
        paramToAdd.parameterType = e.target.checked === true ? EWFParamType.Input : EWFParamType.Bag;
        paramToAdd.isInput = e.target.checked === true;
        this.setState({ paramToAdd });
      }
    } else if (field === "output") {
      if (paramToAdd !== undefined) {
        paramToAdd.isOutput = e.target.checked === true;
        this.setState({ paramToAdd });
      }
    } else if (field === "default") {
      if (paramToAdd !== undefined) {
        paramToAdd.defaultValue = e;
        this.setState({ paramToAdd });
      }
    }
  };

  /**
   * Resolves `true` once the new parameter has been saved and `false` when a check refused it (its
   * warning says why). The create form stays up until its CREATE has held its ✓; it closes itself
   * through `onCreated`.
   */
  addField = async (paramToAdd: IWFParameter): Promise<boolean> => {
    this.setState({ isAddParamInProgress: true });
    const wf = this.props.sd.selectedWf;

    if (wf) {
      wf.parameters === undefined && (wf.parameters = []);
      if (paramToAdd.name === "" || paramToAdd.dataType === undefined) {
        KMainFunctions.displayWarningNotification("Please fill all fields!");
        this.setState({ isAddParamInProgress: false });
        return false;
      }
      const reserved = [...(this.props.sd.project?.commonReservedKeys ?? []), ...WORKFLOW_RESERVED_PARAM_NAMES].map(
        (x) => x.toLowerCase()
      );
      if (reserved.includes((paramToAdd.name ?? "").toLowerCase())) {
        KMainFunctions.displayWarningNotification(
          "C# reserved keywords and system names cannot be used as parameter names."
        );
        this.setState({ isAddParamInProgress: false });
        return false;
      }
      if (
        wf.parameters.some((p) => p.name == paramToAdd.name)
        // && wf.parameters.find((p) => p.name == paramToAdd.name)?.dataType == paramToAdd.dataType
      ) {
        KMainFunctions.displayWarningNotification("Field name already exists!");
        this.setState({ isAddParamInProgress: false });
        return false;
      }
      this.setState({ isAddParamInProgress: false });
      wf.parameters.push(paramToAdd);
      await this.props.sd.saveWorkFlow(wf);
      return true;
    }
    return false;
  };

  handleCreating = (open: boolean, isBO: boolean) => {
    if (open) {
      this.setState({
        addPanelVisible: true,
        paramToAdd: {
          id: Guid.create().toString(),
          name: "",
          dataType: "String" as BackEndValueType,
          parameterType: EWFParamType.Bag,
          isBusinessObject: isBO,
          isInput: false,
          isOutput: false,
          defaultValue: {
            category: ESymbolCategory.fixed,
            label: "Fixed Value",
            description: ""
          }
        }
      });
    } else {
      this.setState({
        addPanelVisible: false
      });
    }
  };

  _getDefaultFixedValue = (dataType: string) => {
    switch (dataType) {
      case "Unknown":
        return "";
      case "Boolean":
        return false;
      case "DateTimeOffset":
        return new Date().toISOString();
      case "Guid":
        return "";
      case "Int16":
      case "Int32":
      case "Int64":
      case "Single":
      case "Decimal":
      case "Double":
        return 0;
      case "String":
        return "";
      default:
        return "";
    }
  };

  /**
   * Resolves `true` once the edit has been saved and `false` when a check refused it (its warning
   * says why), so the card's UPDATE can tell a saved edit from a refused one.
   */
  updateWFParam = async (orgParamId: string, newParam: IWFParameter): Promise<boolean> => {
    const wf = this.props.sd.selectedWf;
    if (wf && wf.parameters && wf.parameters.length > 0) {
      const originalParam = wf.parameters.find((p) => p.id == orgParamId);
      if (newParam.name === "" || newParam.dataType === undefined) {
        KMainFunctions.displayWarningNotification("Please fill all fields!");
        return false;
      }
      if (!/^[A-Za-z][A-Za-z0-9]+$/.test(newParam.name)) {
        KMainFunctions.displayWarningNotification("Field accepts only english letters.");
        return false;
      }
      const reserved = [...(this.props.sd.project?.commonReservedKeys ?? []), ...WORKFLOW_RESERVED_PARAM_NAMES].map(
        (x) => x.toLowerCase()
      );
      if (reserved.includes(newParam.name.toLowerCase())) {
        KMainFunctions.displayWarningNotification(
          "C# reserved keywords and system names cannot be used as parameter names."
        );
        return false;
      }
      if (_.isEqual(originalParam, newParam)) {
        KMainFunctions.displayWarningNotification("There is no change to update!");
        return false;
      }
      if (wf.parameters.some((p) => p.name == newParam.name && p.id !== orgParamId)) {
        KMainFunctions.displayWarningNotification(`There is already a parameter with name "${newParam.name}"!`);
        return false;
      }
      if (originalParam) {
        Object.assign(originalParam, newParam);

        this.props.sd.iterateAllStepsRecursively(wf.nodes?.steps || [], (step) => {
          step?.userInteractions?.forEach((ui) => {
            ui.parameters.forEach((p) => {
              if (p.id === orgParamId) {
                p.value = {
                  ...newParam.defaultValue,
                  label: "Fixed Value",
                  category: ESymbolCategory.fixed,
                  fixedValue: this._getDefaultFixedValue(newParam.dataType)
                } as ISymbol;
              }
            });
          });
        });
        // update all parameters value in user interactions
        this.props.sd.iterateAllStepsRecursively(wf.nodes?.steps || [], (step) => {
          step?.userInteractions?.forEach((ui) => {
            ui.parameters.forEach((p) => {
              if (p.id === orgParamId) {
                p.value = {
                  ...newParam.defaultValue,
                  label: "Fixed Value",
                  category: ESymbolCategory.fixed,
                  fixedValue: this._getDefaultFixedValue(newParam.dataType)
                } as ISymbol;
              }
            });
          });
          const outputParameter = step?.outputParameters?.find((p) => p.id === orgParamId);
          if (outputParameter) {
            outputParameter.value = {
              ...newParam.defaultValue,
              label: "Fixed Value",
              category: ESymbolCategory.fixed,
              fixedValue: this._getDefaultFixedValue(newParam.dataType)
            } as ISymbol;
          }
        });
      }
      await this.props.sd.saveWorkFlow(wf, ESdChangeReason.WFStepMappingChange);
      return true;
    }
    return false;
  };

  /** The card's REMOVE says it is running; the panel no longer dims for it. */
  removeWFParam = async (orgParamId: string): Promise<void> => {
    const wf = this.props.sd.selectedWf;
    if (wf && wf.parameters && wf.parameters.length > 0) {
      let paramIndex = wf.parameters.findIndex((p) => p.id == orgParamId);
      wf.parameters.splice(paramIndex, 1);
      this.props.sd.iterateAllStepsRecursively(wf.nodes?.steps || [], (step) => {
        // if (step.inputParameters.some((p) => p.id !== orgParamId)) {
        //   step.inputParameters.splice(step.inputParameters.findIndex((p) => p.id !== orgParamId), 1)
        // }
        if (step.outputParameters && step.outputParameters.some((p) => p.id === orgParamId)) {
          step.outputParameters.splice(
            step.outputParameters.findIndex((p) => p.id === orgParamId),
            1
          );
        }

        // remove parameter from user interactions
        step?.userInteractions?.forEach((ui) => {
          let uiParamIndex = ui.parameters.findIndex((p) => p.id === orgParamId);
          if (uiParamIndex > -1) {
            ui.parameters.splice(uiParamIndex, 1);
          }
        });
      });
      try {
        await this.props.sd.saveWorkFlow(wf, ESdChangeReason.WFStepMappingChange);
      } catch (error) {
        KMainFunctions.displayErrorNotification(error);
      }
    }
  };

  handleCreateParameters = (e: any, isBO: boolean) => {
    e.stopPropagation();
    if (isBO) {
      this.handleCreating(true, true);
      this.setState((prevState) => ({ ...prevState, activeKey: ["input"] }));
    } else {
      this.handleCreating(true, false);
      this.setState((prevState) => ({ ...prevState, activeKey: ["internal"] }));
    }
  };

  parametersDropdown = (isBO: boolean) => {
    return (
      // The icon paints with `currentColor`, so the colour and its hover state live on this wrapper
      // rather than inline — the `.ant-collapse-extra svg` rule that used to brighten it on hover
      // styled antd's chrome and no longer reaches anything.
      <div
        className="kuika-workflow-parameters__add"
        data-testid={isBO ? "businessObjectPlusButton" : "internalPlusButton"}
        onClick={(e) => this.handleCreateParameters(e, isBO)}
      >
        <PlusIcon style={{ width: 16, height: 16 }} />
      </div>
    );
  };

  /**
   * One parameter's own card. Each is its own single-item collapse rather than one entry in a
   * shared list, because expanding a parameter must not close the one above it.
   */
  private renderParameterCard = (p: IWFParameter, index: number, groupKey: string): ReactNode => (
    <KuikaCollapsePanel
      key={`${groupKey}-${p.id}`}
      className="kuika-workflow-parameters__item-collapse"
      iconPlacement="right"
      items={[
        {
          key: `${p.name}_${p.dataType}_${p.defaultValue?.fixedValue}_${p.parameterType}_${index}`,
          className: "kuika-workflow-parameters-item__panel",
          label: (
            <div className="parameters-item">
              <span className="parameters-item__name">{p.name}</span>
              <span className="parameters-item__type">{p.dataType}</span>
            </div>
          ),
          children: (
            <>
              <span
                data-testid={`workflowParamName-${p.name?.replace(/\s+/g, "").toLowerCase()}`}
                style={{ display: "none" }}
              ></span>
              <WorkflowParameterItem
                property={p}
                onParamUpdated={(o, n) => this.updateWFParam(o, n)}
                onParamDeleted={(e) => this.removeWFParam(e)}
              />
            </>
          )
        }
      ]}
    />
  );

  /** The two groups differ only in which half of the parameter list they hold. */
  private renderParameterGroup = (isBusinessObject: boolean): ReactNode => {
    const { addPanelVisible, paramToAdd } = this.state;
    const wf = this.props.sd.selectedWf;
    const wfParameters: IWFParameter[] = wf && wf.parameters ? wf.parameters : [];
    const groupParameters: IWFParameter[] = wfParameters.filter(
      (p: IWFParameter): boolean => !!p.isBusinessObject === isBusinessObject
    );
    const groupKey: string = isBusinessObject ? "business-object" : "internal";
    // One create form exists at a time, and it belongs to whichever group its parameter is for.
    const isCreatingInThisGroup: boolean = addPanelVisible && !!paramToAdd.isBusinessObject === isBusinessObject;

    return (
      <>
        {isCreatingInThisGroup && (
          <WorkflowParameterItemCreate
            parameter={paramToAdd}
            handleChange={this.handleChange}
            addField={this.addField}
            onCreated={() => this.handleCreating(false, false)}
            onCancel={() => this.handleCreating(false, false)}
          />
        )}
        {groupParameters.map((p: IWFParameter, index: number) => this.renderParameterCard(p, index, groupKey))}
        {groupParameters.length === 0 && !addPanelVisible && (
          <KuikaSmallText className="kuika-workflow-parameters__empty">Empty Parameter List</KuikaSmallText>
        )}
      </>
    );
  };

  render() {
    const wf = this.props.sd.selectedWf;
    const flowName = wf && wf.name;

    return (
      <>
        {this.props.sd.getVisibility(ViewPart.WorkFlowFieldListPanel) === Visibility.Visible && (
          <div className="kuika__workflow__fields kuika-process-fields">
            <div
              style={{
                height: "calc(100vh - 80px)"
              }}
              className="row kuika_topPanel btn-group-vertical kuika_wfParameters"
            >
              <div
                className="kuika_components_right_section kuika_styling_typography_wrapper_row_items_div"
                style={{
                  background: "var(--dark-0)",
                  borderRadius: "var(--radius-l) var(--radius-l) 0px 0px",
                  overflow: "hidden"
                }}
              >
                <>
                  <div className="kuika__workflow__fields__header" id="kuika__workflow__fields__header">
                    <div className="kuika__workflow__fields__header-top">
                      <span style={{ fontSize: 15, fontWeight: 600 }} className="k_Bold-G2-Left">
                        Process Parameters
                      </span>
                    </div>
                    <span style={{ fontSize: 13, color: "var(--dark-5)" }}>{flowName}</span>
                  </div>
                  <div
                    style={{
                      height: "calc(100vh - 140px)",
                      overflowY: "scroll",
                      overflowX: "hidden"
                    }}
                  >
                    <KuikaCollapsePanel
                      data-testid="workflowParametersCollapse"
                      className="kuika-workflow-parameters__collapse"
                      activeKey={this.state.activeKey}
                      onChange={(keys: Key[]): void => this.setState({ activeKey: keys.map(String) })}
                      items={[
                        {
                          key: "input",
                          label: "Business Object",
                          className: "kuika-workflow-parameters__panel",
                          extra: this.parametersDropdown(true),
                          children: this.renderParameterGroup(true)
                        },
                        {
                          key: "internal",
                          label: "Internal",
                          className: "kuika-workflow-parameters__panel",
                          extra: this.parametersDropdown(false),
                          children: this.renderParameterGroup(false)
                        }
                      ]}
                    />
                  </div>
                </>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.WFStepMappingChange) return true;
    return false;
  };
}

const workFlowFieldsPanel = withContext<IWorkFlowFieldsPanelProps>(
  WithForm<IWorkFlowFieldsPanelProps>(withSD<IWorkFlowFieldsPanelProps>(WorkFlowFieldsPanel))
);
export { workFlowFieldsPanel as WorkFlowFieldsPanel };
